/**
 * OBJECTIFS/CORVÉES — écran plein écran côté enfant. Liste les objectifs donnés
 * par papa (dashboard parent) : coche "J'AI FAIT !" (→ done_pending, papa valide),
 * ou réclame la récompense d'un objectif déjà validé (créature garantie).
 */
import { useCallback, useEffect, useState } from "react";

type Item = { id: string; label: string; status: "open" | "done_pending" | "validated"; rewardPending: boolean };
type Drop = { emoji: string; name: string; rarity: string } | null;

export default function Objectives({ apiBase, onClose }: { apiBase: string; onClose: () => void }) {
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [claimed, setClaimed] = useState<{ label: string; drop: Drop } | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${apiBase}/api/child/objectives`);
      if (r.ok) { const d = await r.json(); setItems(d.items || []); }
    } catch { /* offline */ }
    setLoading(false);
  }, [apiBase]);
  useEffect(() => { load(); }, [load]);

  const report = async (id: string) => {
    setItems((cur) => cur.map((i) => (i.id === id ? { ...i, status: "done_pending" } : i)));
    try { await fetch(`${apiBase}/api/child/objectives/report`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) }); } catch { /* ignore */ }
  };
  const claim = async (id: string, label: string) => {
    try {
      const r = await fetch(`${apiBase}/api/child/objectives/claim`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ id }) });
      const d = await r.json();
      if (d?.ok) { setClaimed({ label, drop: d.drop }); setItems((cur) => cur.filter((i) => i.id !== id)); }
    } catch { /* ignore */ }
  };

  const rar = claimed?.drop?.rarity;
  const col = rar === "legendary" ? "#ffd43b" : rar === "rare" ? "var(--kid-cyan)" : "var(--kid-text-3)";

  return (
    <div style={S.screen}>
      <div style={S.header}>
        <button style={S.close} onClick={onClose} aria-label="Fermer">✕</button>
        <div style={S.title}>🎯 MES OBJECTIFS</div>
        <div style={{ width: 40 }} />
      </div>
      <div style={S.scroll}>
        {loading && <div style={S.empty}>Chargement…</div>}
        {!loading && items.length === 0 && <div style={S.empty}>Pas d'objectif pour l'instant — ton papa peut t'en donner un depuis son tableau de bord !</div>}
        {items.map((it) => (
          <div key={it.id} style={S.card}>
            <div style={S.label}>{it.label}</div>
            {it.status === "open" && <button style={S.doneBtn} onClick={() => report(it.id)}>✅ J'AI FAIT !</button>}
            {it.status === "done_pending" && <div style={S.pending}>⏳ En attente de validation par papa…</div>}
            {it.status === "validated" && it.rewardPending && (
              <button style={S.doneBtn} onClick={() => claim(it.id, it.label)}>🎁 Récupérer ma récompense !</button>
            )}
          </div>
        ))}
      </div>

      {claimed && (
        <div style={S.winOverlay} onClick={() => setClaimed(null)}>
          <div style={{ ...S.winCard, borderColor: col }}>
            <div style={{ fontSize: 40 }}>🏆</div>
            <div style={{ color: col, fontWeight: 800, fontSize: 18 }}>OBJECTIF VALIDÉ !</div>
            <div style={{ fontSize: 13, color: "var(--kid-text-3)", marginTop: 4 }}>« {claimed.label} »</div>
            {claimed.drop ? (
              <div style={{ margin: "16px auto 4px" }}>
                <div style={{ fontSize: 44 }}>{claimed.drop.emoji}</div>
                <div style={{ fontWeight: 700 }}>{claimed.drop.name}</div>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--kid-text-3)", marginTop: 10 }}>Collection déjà complète, bravo !</div>
            )}
            <button style={S.doneBtn} onClick={() => setClaimed(null)}>Continuer</button>
          </div>
        </div>
      )}
    </div>
  );
}

const S: Record<string, React.CSSProperties> = {
  screen: { position: "fixed", inset: 0, zIndex: 61, background: "var(--kid-bg)", display: "flex", flexDirection: "column", color: "var(--kid-text)", fontFamily: "var(--kid-font-sans, system-ui)" },
  header: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px 16px 8px", borderBottom: "1px solid var(--kid-border)" },
  close: { width: 40, height: 40, borderRadius: 12, border: "1px solid var(--kid-cyan-40)", background: "transparent", color: "var(--kid-cyan)", fontSize: 18, cursor: "pointer" },
  title: { fontFamily: "var(--kid-font-mono)", letterSpacing: "0.14em", fontSize: 15, color: "var(--kid-cyan)" },
  scroll: { flex: 1, overflowY: "auto", padding: "16px" },
  empty: { color: "var(--kid-text-3)", fontSize: 14, textAlign: "center", padding: 30, lineHeight: 1.5 },
  card: { background: "rgba(255,255,255,0.04)", border: "1.5px solid var(--kid-cyan-40)", borderRadius: 16, padding: "14px 16px", marginBottom: 12 },
  label: { fontSize: 16, fontWeight: 700, marginBottom: 10 },
  doneBtn: { width: "100%", padding: "12px", borderRadius: 12, border: "none", background: "linear-gradient(180deg, var(--kid-cyan), #008cbe)", color: "#04222c", fontSize: 15, fontWeight: 800, cursor: "pointer" },
  pending: { color: "#ffb84d", fontSize: 13, fontWeight: 600 },
  winOverlay: { position: "fixed", inset: 0, zIndex: 64, background: "rgba(3,5,9,0.8)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 },
  winCard: { textAlign: "center", padding: "26px 30px", borderRadius: 20, border: "2px solid", background: "var(--kid-bg)", maxWidth: 340, width: "100%" },
};
